import { useState } from "react";
import { findRivalry } from "@averagejoesff/shared";
import type { MatchupReport } from "@averagejoesff/shared";
import { StateMessage } from "../../components/StateMessage";
import { MatchupHeader } from "./MatchupHeader";
import { RivalryHistory, RivalrySummary } from "./RivalryPanel";
import type { ManagerLookup } from "./managers";
import { managerName } from "./managers";

interface HeadToHeadExplorerProps {
  report: MatchupReport;
  managers: ManagerLookup;
}

/** Pick any two managers and see their all-time history. */
export function HeadToHeadExplorer({ report, managers }: HeadToHeadExplorerProps) {
  // Start on the week's first game — the pair most people will look up first.
  const [initialLeft, initialRight] = report.matchups[0]?.managerIds ?? [
    report.managers[0]?.id ?? "",
    report.managers[1]?.id ?? "",
  ];
  const [leftId, setLeftId] = useState(initialLeft);
  const [rightId, setRightId] = useState(initialRight);

  const options = [...report.managers].sort((a, b) => a.name.localeCompare(b.name));
  const rivalry = leftId !== rightId ? findRivalry(report, leftId, rightId) : null;

  function swap() {
    setLeftId(rightId);
    setRightId(leftId);
  }

  const picker = (value: string, onChange: (id: string) => void, label: string) => (
    <label className="h2h__picker">
      <span className="h2h__picker-label">{label}</span>
      <select className="season-picker__select" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="h2h">
      <div className="h2h__controls">
        {picker(leftId, setLeftId, "Manager")}
        <button type="button" className="h2h__swap" onClick={swap} aria-label="Swap managers">
          ⇄
        </button>
        {picker(rightId, setRightId, "Opponent")}
      </div>

      {leftId === rightId ? (
        <StateMessage variant="empty" title="Pick two different managers" />
      ) : (
        <article className="matchup-card">
          <MatchupHeader left={managers.get(leftId)} right={managers.get(rightId)} />
          {rivalry?.nickname && (
            <div className="matchup-card__billing">
              <span className="matchup-card__nickname">{rivalry.nickname}</span>
            </div>
          )}
          {rivalry ? (
            // Someone picked this pair on purpose, so show everything expanded.
            <>
              <RivalrySummary rivalry={rivalry} managers={managers} />
              <RivalryHistory rivalry={rivalry} managers={managers} />
            </>
          ) : (
            <StateMessage
              variant="empty"
              title="They've never met"
              detail={`${managerName(managers, leftId)} and ${managerName(managers, rightId)} have no games against each other (${report.coverage}).`}
            />
          )}
        </article>
      )}
    </div>
  );
}
